import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { CalendarCheck, BedDouble, Compass, Ship, ArrowUpRight } from "lucide-react";
import { IMG, SERVICES, SEGMENTS, TOURS, EVENTS_PLANNING, STEPS } from "@/data/content";
import { SectionLabel, Reveal } from "@/components/wf/primitives";
import { PayButton } from "@/components/wf/PayButton";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
const ICONS = [CalendarCheck, BedDouble, Compass, Ship];

export const Services = () => (
  <section id="services" className="py-24 lg:py-32 border-t border-brand-teal/15" data-testid="services-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="max-w-3xl">
        <SectionLabel testid="services-label">What we do</SectionLabel>
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-sand leading-[1.05]">
            Four disciplines, <span className="italic text-brand-aqua">one</span> accountable operator.
          </h2>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-2 mt-16 border-t border-l border-brand-teal/20">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[i];
          return (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="group relative border-b border-r border-brand-teal/20 p-8 lg:p-12 h-full hover:bg-surface transition-colors duration-500" data-testid={`service-card-${i}`}>
                <div className="flex items-start justify-between">
                  <span className="font-serif text-5xl text-brand-teal/40 group-hover:text-brand-coral transition-colors">{s.n}</span>
                  <Icon className="w-7 h-7 text-brand-aqua" strokeWidth={1.4} />
                </div>
                <h3 className="mt-8 font-serif text-2xl lg:text-3xl text-brand-sand tracking-tight">{s.title}</h3>
                <p className="mt-4 font-sans text-brand-sand/70 leading-relaxed">{s.body}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

export const WhoWeHelp = () => (
  <section id="who" className="py-24 lg:py-32 bg-surface border-t border-brand-teal/15" data-testid="who-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5">
        <SectionLabel testid="who-label">Who we help</SectionLabel>
        <Reveal>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-brand-sand leading-[1.05]">
            Built for the clients who <span className="italic text-brand-aqua">can't afford</span> for it to go wrong.
          </h2>
          <p className="mt-6 font-sans text-brand-sand/70 leading-relaxed max-w-md">
            Institutional clients are our anchor market. When a delegation lands or a mission begins,
            there is one local operator answerable for every moving part.
          </p>
        </Reveal>
      </div>
      <div className="lg:col-span-7 lg:pt-4">
        {SEGMENTS.map((seg, i) => (
          <Reveal key={seg.k} delay={i * 0.06}>
            <div className="group flex items-baseline gap-6 py-6 border-t border-brand-teal/20 last:border-b" data-testid={`segment-${i}`}>
              <span className="font-serif text-sm text-brand-teal w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div className="flex-1">
                <h3 className="font-serif text-xl lg:text-2xl text-brand-sand group-hover:text-brand-aqua transition-colors">{seg.k}</h3>
                <p className="mt-1 font-sans text-sm text-brand-sand/60">{seg.v}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Tours = () => (
  <section id="tours" className="py-24 lg:py-32 border-t border-brand-teal/15" data-testid="tours-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-8 items-end">
        <div className="lg:col-span-8">
          <SectionLabel testid="tours-label">Set-departure tours</SectionLabel>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-sand leading-[1.05]">
            Pick a departure. We do the rest.
          </h2>
        </div>
        <p className="lg:col-span-4 font-sans text-brand-sand/70 leading-relaxed">
          Choose a fixed departure and book your own flights to the meeting point. From there,
          WayFinder runs everything — accommodation, transfers, guiding and the full itinerary.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-px mt-16 bg-brand-teal/20">
        {TOURS.map((t, i) => (
          <Reveal key={t.title} delay={i * 0.1}>
            <div className="bg-brand-navy h-full flex flex-col" data-testid={`tour-card-${i}`}>
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={IMG[t.img]} alt={t.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 to-transparent" />
                <span className="absolute bottom-4 left-4 font-sans text-xs tracking-[0.2em] uppercase text-brand-aqua">{t.duration}</span>
              </div>
              <div className="p-8 flex flex-col flex-1">
                <h3 className="font-serif text-2xl text-brand-sand tracking-tight">{t.title}</h3>
                <p className="mt-3 font-sans text-sm text-brand-sand/60">{t.route}</p>
                <p className="mt-2 font-sans text-sm text-brand-teal">{t.departure}</p>
                <button onClick={() => scrollTo("contact")} data-testid={`tour-enquire-${i}`}
                  className="group mt-8 inline-flex items-center justify-between gap-2 border border-brand-teal/40 px-5 py-3 text-brand-sand hover:bg-brand-coral hover:text-brand-navy hover:border-brand-coral transition-colors">
                  <span className="font-sans text-sm">Enquire for pricing</span>
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
                </button>
                <div className="mt-4 pt-4 border-t border-brand-teal/15">
                  <PayButton itemName={`Deposit — ${t.title}`} label="Pay a deposit" testid={`tour-pay-${i}`} />
                </div>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const SignatureEvents = () => (
  <section id="events" className="border-t border-brand-teal/15" data-testid="events-section">
    <div className="py-6 border-b border-brand-teal/15 overflow-hidden">
      <Marquee speed={26} gradient={false} autoFill>
        <span className="font-sans text-sm tracking-[0.3em] uppercase text-brand-teal/60 mx-8">The Runway Marathon · Tuvalu ✦ World's fourth-smallest country ✦ Lagoon on one side, open ocean on the other ✦&nbsp;</span>
      </Marquee>
    </div>

    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
      <SectionLabel testid="events-label">Signature events</SectionLabel>

      <Reveal>
        <div className="relative grid lg:grid-cols-2 border border-brand-teal/20" data-testid="flagship-event">
          <div className="relative aspect-[16/11] lg:aspect-auto overflow-hidden">
            <img src={IMG.runway} alt="Funafuti's international runway beside the lagoon" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-brand-navy/40" />
          </div>
          <div className="p-8 lg:p-14 flex flex-col justify-center">
            <span className="font-sans text-xs tracking-[0.28em] uppercase text-brand-coral">Flagship event</span>
            <h3 className="mt-4 font-serif text-4xl lg:text-5xl text-brand-sand tracking-tight leading-[1.05]">The Runway Marathon, Tuvalu</h3>
            <p className="mt-6 font-sans text-brand-sand/70 leading-relaxed">
              A full marathon looping Funafuti's international runway — which doubles as the nation's public park —
              with the lagoon on one side and the open ocean on the other, in the world's fourth-smallest and
              least-visited country.
            </p>
            <button onClick={() => scrollTo("contact")} data-testid="flagship-register-cta"
              className="group mt-10 self-start inline-flex items-center gap-2 px-7 py-4 bg-brand-coral text-brand-navy font-medium hover:bg-brand-sand transition-colors">
              Register interest <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
            </button>
            <div className="mt-6">
              <PayButton itemName="Runway Marathon Tuvalu — entry deposit" label="Pay entry / deposit" testid="flagship-pay" />
            </div>
          </div>
        </div>
      </Reveal>

      <p className="mt-16 mb-8 font-sans text-sm tracking-[0.2em] uppercase text-brand-teal">In planning</p>
      <div className="grid md:grid-cols-3 border-t border-l border-brand-teal/20">
        {EVENTS_PLANNING.map((e, i) => (
          <Reveal key={e.title} delay={i * 0.08}>
            <div className="group border-b border-r border-brand-teal/20 p-8 h-full hover:bg-surface transition-colors" data-testid={`planning-event-${i}`}>
              <h4 className="font-serif text-2xl text-brand-sand tracking-tight">{e.title}</h4>
              <p className="mt-3 font-sans text-sm text-brand-sand/60 leading-relaxed">{e.body}</p>
              <button onClick={() => scrollTo("contact")} data-testid={`planning-register-${i}`}
                className="mt-6 inline-flex items-center gap-2 font-sans text-sm text-brand-aqua hover:text-brand-coral transition-colors">
                Register interest <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const HowItWorks = () => (
  <section id="how" className="py-24 lg:py-32 bg-surface border-t border-brand-teal/15" data-testid="how-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <SectionLabel testid="how-label">How it works</SectionLabel>
      <Reveal>
        <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-sand leading-[1.05] max-w-3xl">
          A course set in four moves.
        </h2>
      </Reveal>
      <div className="grid md:grid-cols-4 mt-16 gap-px bg-brand-teal/20">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.1}>
            <div className="bg-surface p-8 h-full relative" data-testid={`step-${i}`}>
              <span className="font-serif text-6xl text-brand-teal/30">{s.n}</span>
              <div className="mt-4 h-px w-full bg-brand-teal/20 relative">
                <span className="absolute left-0 -top-1 w-2 h-2 rounded-full bg-brand-coral" />
              </div>
              <h3 className="mt-6 font-serif text-xl lg:text-2xl text-brand-sand">{s.title}</h3>
              <p className="mt-3 font-sans text-sm text-brand-sand/60 leading-relaxed">{s.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const GALLERY = ["teAno", "faleSunset", "causeway", "pristineBeach", "roadAtoll", "townAerial"];
export const Gallery = () => (
  <section id="gallery" className="py-24 lg:py-32 border-t border-brand-teal/15" data-testid="gallery-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <SectionLabel testid="gallery-label">Gallery</SectionLabel>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-sand leading-[1.05]">From the field.</h2>
        </div>
        <p className="max-w-sm font-sans text-brand-sand/60">Events, tours and voyages across the moana — a living record of the work.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-16">
        {GALLERY.map((g, i) => (
          <Reveal key={g} delay={(i % 3) * 0.08}>
            <div className={`relative overflow-hidden ${i % 5 === 0 ? "aspect-[4/5]" : "aspect-square"}`} data-testid={`gallery-item-${i}`}>
              <img src={IMG[g]} alt="WayFinder Pacific field imagery" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
